import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Check } from "lucide-react";
import { toast } from "sonner";
import { devLog } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange, size = 16 }) => {
  const interactive = !!onChange;
  return (
    <div
      data-testid="review-stars"
      className={`inline-flex items-center gap-0.5 ${
        interactive ? "" : "text-[var(--ld-gold-deep)]"
      }`}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        const StarEl = (
          <Star
            key={n}
            size={size}
            strokeWidth={1.4}
            className={
              filled
                ? "text-[var(--ld-gold-deep)] fill-[var(--ld-gold-deep)]"
                : "text-[var(--ld-ink)]/25"
            }
          />
        );
        if (!interactive) return StarEl;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            data-testid={`review-star-${n}`}
            aria-label={`${n} stars`}
            className="p-0.5 focus:outline-none focus:ring-1 focus:ring-[var(--ld-gold-deep)]"
          >
            {StarEl}
          </button>
        );
      })}
    </div>
  );
};

const ReviewForm = ({ slug, onSubmitted }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !comment.trim()) {
      toast("Add your name and a short comment", { duration: 2400 });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/vendors/${slug}/reviews`, {
        reviewer_name: name.trim(),
        reviewer_email: email.trim(),
        rating,
        comment: comment.trim(),
      });
      setDone(true);
      setName("");
      setEmail("");
      setComment("");
      setRating(5);
      onSubmitted?.();
      toast("Thanks — your review is pending review", { duration: 3000 });
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't submit", {
        duration: 2800,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="review-submitted"
        className="border border-[var(--ld-border)] p-6 text-center"
      >
        <Check
          size={24}
          strokeWidth={1.4}
          className="text-[var(--ld-gold-deep)] mx-auto"
        />
        <p className="mt-3 font-serif-display text-xl text-[var(--ld-ink)]">
          Thank you.
        </p>
        <p className="mt-2 text-sm text-[var(--ld-ink-muted)]">
          Your review is pending approval and will appear shortly.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="review-form"
      onSubmit={submit}
      className="border border-[var(--ld-border)] p-6 space-y-4"
    >
      <div>
        <p className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)] mb-2">
          Your rating
        </p>
        <Stars value={rating} onChange={setRating} size={22} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          data-testid="review-name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="h-11 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
        />
        <input
          data-testid="review-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional)"
          className="h-11 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
        />
      </div>
      <textarea
        data-testid="review-comment"
        required
        rows={4}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Share a few words about your experience…"
        className="w-full bg-transparent border border-[var(--ld-ink)]/25 px-3 py-2 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
      />
      <button
        type="submit"
        disabled={submitting}
        data-testid="review-submit"
        className="h-11 px-6 text-[12px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-ivory)] hover:bg-[var(--ld-gold-deep)] transition-colors disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Submit review"}
      </button>
      <p className="text-[11px] text-[var(--ld-ink-muted)]">
        Reviews are moderated before being published.
      </p>
    </form>
  );
};

const VendorReviews = ({ slug }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vendors/${slug}/reviews`);
      setReviews(r.data || []);
    } catch (e) {
      devLog("ld_reviews load failed:", e?.message);
      // empty state covers this
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // Reload only when the vendor slug changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const avg =
    reviews.length === 0
      ? 0
      : reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length;

  return (
    <section
      data-testid="vendor-reviews-section"
      className="relative px-6 md:px-12 py-16 md:py-24 bg-white/40 border-y border-[var(--ld-border)]"
    >
      <div className="mx-auto max-w-3xl">
        <div className="text-center">
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            Reviews
          </span>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
            What couples are saying
          </h2>
          {reviews.length > 0 && (
            <div className="mt-4 flex flex-col items-center gap-2">
              <Stars value={Math.round(avg)} size={20} />
              <p className="text-[12px] tracking-[0.15em] uppercase text-[var(--ld-ink-muted)]">
                {avg.toFixed(1)} · {reviews.length}{" "}
                {reviews.length === 1 ? "review" : "reviews"}
              </p>
            </div>
          )}
          <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
        </div>

        <ul data-testid="vendor-reviews-list" className="mt-10 space-y-6">
          {loading && (
            <li className="text-center text-sm text-[var(--ld-ink-muted)]">
              Loading reviews…
            </li>
          )}
          {!loading && reviews.length === 0 && (
            <li className="text-center text-sm text-[var(--ld-ink-muted)] italic">
              No reviews yet — be the first to share your experience.
            </li>
          )}
          {reviews.map((r) => (
            <li
              key={r.id}
              data-testid={`review-item-${r.id}`}
              className="border border-[var(--ld-border)] p-6 bg-white/60"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="font-serif-display text-lg text-[var(--ld-ink)]">
                  {r.reviewer_name}
                </p>
                <Stars value={r.rating} size={14} />
              </div>
              <p className="mt-3 text-[14.5px] leading-[1.75] text-[var(--ld-ink)]/85 whitespace-pre-wrap">
                {r.comment}
              </p>
              {r.submitted_at && (
                <p className="mt-3 text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
                  {new Date(r.submitted_at).toLocaleDateString()}
                </p>
              )}
            </li>
          ))}
        </ul>

        <div className="mt-12">
          <h3 className="font-serif-display text-2xl text-[var(--ld-ink)] mb-4">
            Leave a review
          </h3>
          <ReviewForm slug={slug} onSubmitted={reload} />
        </div>
      </div>
    </section>
  );
};

export default VendorReviews;
